import autobind from 'autobind';

import type { UpdateFieldForm } from 'chaika-frontend-types/frontend-cms';
import type { FieldsUpdateParams } from 'chaika-types';

import { LazyUpdater } from '@utils/LazyUpdater';
import { EntryApi } from '@api';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';

export interface EntryUpdateData {
    id: string;
    ehrId: string;
    updateData: UpdateFieldParams;
}

export interface UpdateFieldParams {
    [fieldName: string]: UpdateFieldForm;
}

export class EntryLazyUpdater extends LazyUpdater<EntryUpdateData> {
    protected async sendUpdateRequest({ id, ehrId, updateData = {} }: EntryUpdateData): Promise<void> {
        const fields: FieldsUpdateParams[] = [];
        Object.keys(updateData).forEach(fieldName => {
            fields.push({
                fieldName,
                value: updateData[fieldName].field.value,
            });
        });

        await store.app.doOrShowError({
            allowMultiAction: true,
            showLoader: false,
            errorHandler: e => this.onUpdateFieldError(e, fields),
            action: () => EntryApi.updateField(id, fields, ehrId),
        });
    }

    protected mergeData(
        { updateData: currentUpdateData, ...currentData }: EntryUpdateData,
        { updateData: newUpdateData }: EntryUpdateData,
    ): EntryUpdateData {
        return {
            ...currentData,
            updateData: {
                ...currentUpdateData,
                ...newUpdateData,
            },
        };
    }

    @autobind
    private onUpdateFieldError(error: any, fields: FieldsUpdateParams[]): void {
        const {
            app,
            entry: { entryType },
        } = store;
        const fieldsHumanRead: string[] = fields.map(({ fieldName, value }) => {
            const name =
                entryType.getFieldByName(fieldName)?.title ??
                entryType.getFieldBlockTitleByName(fieldName) ??
                fieldName;

            return `${name}: ${value}`;
        });
        app.showPopup({
            title: translate('filedsUpdateError'),
            description: `${translate('posibleChangesNotSaveText')}
${fieldsHumanRead.join('\n')}

${translate('error')}: ${error.message}

${translate('canRefrashToCheckText')}`,
        });
    }
}

export const entryLazyUpdater = new EntryLazyUpdater();
